import React, { useMemo, useState } from "react";
import "./ClientWiseSummary.css";

const ClientWiseSummary = ({ clientData = [] }) => {

    const [sortConfig, setSortConfig] = useState({
        key: "clientName",
        direction: "asc"
    });


    const handleSort = (key) => {

        setSortConfig((current) => {

            if (current.key === key) {
                return {
                    key,
                    direction:
                        current.direction === "asc"
                            ? "desc"
                            : "asc"
                };
            }

            return {
                key,
                direction: "asc"
            };
        });
    };


    const sortedData = useMemo(() => {

        const data = [...clientData];

        if (!sortConfig.key) {
            return data;
        }

        data.sort((a, b) => {

            let valueA = a[sortConfig.key];
            let valueB = b[sortConfig.key];


            /*
             * Client name
             */
            if (sortConfig.key === "clientName") {

                return String(valueA ?? "")
                    .localeCompare(
                        String(valueB ?? ""),
                        undefined,
                        {
                            sensitivity: "base"
                        }
                    ) *
                    (sortConfig.direction === "asc"
                        ? 1
                        : -1);
            }


            /*
             * Numeric columns
             */
            valueA = Number(valueA) || 0;
            valueB = Number(valueB) || 0;

            return (
                valueA - valueB
            ) *
            (
                sortConfig.direction === "asc"
                    ? 1
                    : -1
            );
        });

        return data;

    }, [clientData, sortConfig]);


    const getSortIcon = (key) => {

        if (sortConfig.key !== key) {
            return "↕";
        }

        return sortConfig.direction === "asc"
            ? "↑"
            : "↓";
    };


    const formatNumber = (value) => {

        return Number(value || 0)
            .toLocaleString("en-IN");
    };


    const getPercentage = (value, total) => {

        if (!total || total === 0) {
            return "0%";
        }

        return (
            (Number(value || 0) /
                Number(total || 0)) *
            100
        ).toFixed(1) + "%";
    };


    return (
        <div className="client-wise-card">

            {/* HEADER */}

            <div className="client-wise-header">

                <div>
                    <h3>
                        Client Wise Report
                    </h3>

                    <span>
                        Communication summary by client
                    </span>
                </div>

            </div>


            {/* TABLE */}

            <div className="client-wise-table-wrapper">

                <table className="client-wise-table">

                    <thead>

                        <tr>

                            <th
                                onClick={() =>
                                    handleSort(
                                        "clientName"
                                    )
                                }
                            >
                                <div className="sortable-header">
                                    <span>
                                        Client Name
                                    </span>

                                    <span className="sort-icon">
                                        {getSortIcon(
                                            "clientName"
                                        )}
                                    </span>
                                </div>
                            </th>


                            <th
                                onClick={() =>
                                    handleSort(
                                        "request"
                                    )
                                }
                            >
                                <div className="sortable-header">
                                    <span>
                                        Total Request
                                    </span>

                                    <span className="sort-icon">
                                        {getSortIcon(
                                            "request"
                                        )}
                                    </span>
                                </div>
                            </th>


                            <th
                                onClick={() =>
                                    handleSort(
                                        "rejected"
                                    )
                                }
                            >
                                <div className="sortable-header">
                                    <span>
                                        Total Rejected
                                    </span>

                                    <span className="sort-icon">
                                        {getSortIcon(
                                            "rejected"
                                        )}
                                    </span>
                                </div>
                            </th>


                            <th
                                onClick={() =>
                                    handleSort(
                                        "submit"
                                    )
                                }
                            >
                                <div className="sortable-header">
                                    <span>
                                        Total Submit
                                    </span>

                                    <span className="sort-icon">
                                        {getSortIcon(
                                            "submit"
                                        )}
                                    </span>
                                </div>
                            </th>


                            <th
                                onClick={() =>
                                    handleSort(
                                        "delivered"
                                    )
                                }
                            >
                                <div className="sortable-header">
                                    <span>
                                        Total Delivered
                                    </span>

                                    <span className="sort-icon">
                                        {getSortIcon(
                                            "delivered"
                                        )}
                                    </span>
                                </div>
                            </th>


                            <th
                                onClick={() =>
                                    handleSort(
                                        "failed"
                                    )
                                }
                            >
                                <div className="sortable-header">
                                    <span>
                                        Total Failed
                                    </span>

                                    <span className="sort-icon">
                                        {getSortIcon(
                                            "failed"
                                        )}
                                    </span>
                                </div>
                            </th>


                            <th
                                onClick={() =>
                                    handleSort(
                                        "awaited"
                                    )
                                }
                            >
                                <div className="sortable-header">
                                    <span>
                                        Total Awaited
                                    </span>

                                    <span className="sort-icon">
                                        {getSortIcon(
                                            "awaited"
                                        )}
                                    </span>
                                </div>
                            </th>

                        </tr>

                    </thead>


                    <tbody>

                        {sortedData.length === 0 ? (

                            <tr>

                                <td
                                    colSpan="7"
                                    className="no-client-data"
                                >
                                    No client data available
                                </td>

                            </tr>

                        ) : (

                            sortedData.map(
                                (client, index) => (

                                    <tr
                                        key={
                                            client.id ||
                                            client.clientName ||
                                            index
                                        }
                                    >

                                        <td className="client-name">
                                            {client.clientName}
                                        </td>


                                        <td>
                                            {formatNumber(
                                                client.request
                                            )}
                                        </td>


                                        <td>
                                            {formatNumber(
                                                client.rejected
                                            )}
                                        </td>


                                        <td>
                                            {formatNumber(
                                                client.submit
                                            )}
                                        </td>


                                        <td>

                                            <div className="value-with-percentage">

                                                <span>
                                                    {formatNumber(
                                                        client.delivered
                                                    )}
                                                </span>

                                                <span className="delivered-percentage">
                                                    {getPercentage(
                                                        client.delivered,
                                                        client.submit
                                                    )}
                                                </span>

                                            </div>

                                        </td>


                                        <td>

                                            <div className="value-with-percentage">

                                                <span>
                                                    {formatNumber(
                                                        client.failed
                                                    )}
                                                </span>

                                                <span className="failed-percentage">
                                                    {getPercentage(
                                                        client.failed,
                                                        client.submit
                                                    )}
                                                </span>

                                            </div>

                                        </td>


                                        <td>

                                            <div className="value-with-percentage">

                                                <span>
                                                    {formatNumber(
                                                        client.awaited
                                                    )}
                                                </span>

                                                <span className="awaited-percentage">
                                                    {getPercentage(
                                                        client.awaited,
                                                        client.submit
                                                    )}
                                                </span>

                                            </div>

                                        </td>

                                    </tr>

                                )
                            )

                        )}

                    </tbody>

                </table>

            </div>

        </div>
    );
};

export default ClientWiseSummary;